// src/store/slices/cartSlice.js
// Local cart state — no server-side cart needed for the union store.
// Cart is persisted to localStorage via redux-persist.

import { createSlice } from "@reduxjs/toolkit";

const cartSlice = createSlice({
  name: "cart",
  initialState: {
    items: [],
  },
  reducers: {
    /**
     * Add a product to the cart.
     * If the same productId + size combo already exists, increment quantity.
     */
    addToCart: (state, action) => {
      const { productId, productName, productImageUrl, unitPrice, quantity = 1, size = null, customizationText = null } = action.payload;

      const existingIndex = state.items.findIndex(
        (item) => item.productId === productId && item.size === size
      );

      if (existingIndex >= 0) {
        state.items[existingIndex].quantity += quantity;
      } else {
        state.items.push({ productId, productName, productImageUrl, unitPrice, quantity, size, customizationText });
      }
    },

    /**
     * Remove a specific line item from the cart (matched by productId + size).
     */
    removeFromCart: (state, action) => {
      const { productId, size = null } = action.payload;
      state.items = state.items.filter(
        (item) => !(item.productId === productId && item.size === size)
      );
    },

    /**
     * Set the absolute quantity for a cart line item.
     * If quantity is 0 or less, the item is removed.
     */
    updateQuantity: (state, action) => {
      const { productId, size = null, quantity } = action.payload;
      const index = state.items.findIndex(
        (item) => item.productId === productId && item.size === size
      );
      if (index < 0) return;

      if (quantity <= 0) {
        state.items.splice(index, 1);
      } else {
        state.items[index].quantity = quantity;
      }
    },

    /**
     * Clear all items from the cart (called after successful order placement).
     */
    clearCart: (state) => {
      state.items = [];
    },
  },
});

export const { addToCart, removeFromCart, updateQuantity, clearCart } = cartSlice.actions;

// ─── Selectors ────────────────────────────────────────────────────────────────

export const selectCartItems = (state) => state.cart.items;

export const selectCartItemCount = (state) =>
  state.cart.items.reduce((sum, item) => sum + item.quantity, 0);

export const selectCartTotal = (state) =>
  state.cart.items.reduce((sum, item) => sum + item.unitPrice * item.quantity, 0);

export const selectCartHasItem = (productId, size = null) => (state) =>
  state.cart.items.some((item) => item.productId === productId && item.size === size);

export default cartSlice.reducer;
